import classNames from 'classnames';
import { getSearch } from 'connected-react-router';
import { isEmpty } from 'lodash';
import { FC } from 'react';
import { useIntl } from 'react-intl';
import { useSelector, connect } from 'react-redux';
import { Link } from 'react-router-dom';

import styles from './product.module.scss';

import { ProductModel } from '../../../../models/products.model';
import { updateQueryString } from '../../../../providers/location.provider';
import { AppState } from '../../../../store/store';
import { useMedia } from '../../../context/InterfaceContext';
import { PRODUCT_PATH } from '../../Navigator';
import AddButton from '../addButton';
import Available from '../available';
import ExchangeDuration from '../ExchangeDuration';
import Id from '../id';
import Price from '../Price';
import RemoveButton from '../removeButton';
import StrongText from '../StrongText';

interface OwnProps {
    collectionStyling?: boolean;
    horizontalMode?: boolean;
    loadingProduct?: boolean;
    product: ProductModel;
    removable?: boolean;
}

type Props = OwnProps;

const Product: FC<Props> = ({ product, horizontalMode = false, removable = false, collectionStyling = false }) => {
    const intl = useIntl();
    const searchState = useSelector((state: AppState) => getSearch(state));
    const { isMobile, isMobileMiddle } = useMedia();

    const getFormat = (format: string) => {
        if (collectionStyling) {
            return <h3 className={styles.collection_format}>{format}</h3>;
        }
        return <StrongText className={styles.format}>{format}</StrongText>;
    };

    if (!isEmpty(product)) {
        const {
            productphotopath,
            format,
            formatcode,
            address,
            city,
            mediainfo,
            validfrom,
            validto,
            producturn,
            pricedays,
            durationpossible,
            priceinformation,
            mediacount,
            producttype,
            size,
        } = product;
        const search = updateQueryString(searchState, { urn: producturn });
        const isShowSize = ['MPO', 'BPO'].includes(formatcode);

        return (
            <div
                className={classNames(styles.wrapper, {
                    [styles.horizontalMode]: isMobile || isMobileMiddle ? false : horizontalMode,
                })}
            >
                <Link
                    to={{ pathname: PRODUCT_PATH, search }}
                    className={classNames({
                        [styles.listViewImage]: !collectionStyling && horizontalMode,
                    })}
                >
                    <div className={styles.imgWrapper}>
                        <img src={`${productphotopath}&resolution=preview`} alt="Product image" loading="lazy" />
                    </div>
                </Link>

                <div className={styles.descriptionWrapper}>
                    <Link to={{ pathname: PRODUCT_PATH, search }} className={styles.description}>
                        <div className={styles.descriptionInnerWrapper}>
                            <div>{getFormat(format)}</div>
                            <div className={styles.rowId}>
                                <Id id={String(mediainfo.nummer)} intl={intl} collectionStyling={collectionStyling} />

                                <Available from={validfrom} to={validto} intl={intl} />
                            </div>
                            {collectionStyling && <div className={styles.collectionSeparationLine}></div>}

                            <div className={styles.address}>
                                {address}
                                <div>{city}</div>
                                {size > 0 && isShowSize && (
                                    <div>
                                        {intl.formatMessage({ id: 'product.size', defaultMessage: 'Size:' })} {size} m²
                                    </div>
                                )}
                                {mediacount && (
                                    <div style={{ marginTop: 22 }}>
                                        {producttype === 'digital'
                                            ? intl.formatMessage(
                                                  {
                                                      id: 'general.mediaCountDigital',
                                                      defaultMessage: '<b>Anzahl Screens:</b> {mediacount}',
                                                  },
                                                  { mediacount, b: value => <b style={{ fontWeight: 500 }}>{value}</b> }
                                              )
                                            : intl.formatMessage(
                                                  {
                                                      id: 'general.mediaCountAnalog',
                                                      defaultMessage: '<b>Anzahl Flächen:</b> {mediacount}',
                                                  },
                                                  { mediacount, b: value => <b style={{ fontWeight: 500 }}>{value}</b> }
                                              )}
                                    </div>
                                )}
                            </div>

                            <ExchangeDuration duration={durationpossible} intl={intl} className={styles.exchangeDuration} />
                        </div>

                        <Price
                            className={styles.price}
                            priceinformation={priceinformation}
                            withGross
                            duration={pricedays}
                            withEmptyText
                            intl={intl}
                            collectionStyling={collectionStyling}
                        />
                    </Link>
                    {horizontalMode && !removable && (
                        <div className={styles.addContainerWrapper}>
                            <AddButton
                                className={styles.add}
                                urn={producturn}
                                removable={removable}
                                horizontalMode={isMobile || isMobileMiddle ? true : horizontalMode}
                            />
                        </div>
                    )}
                </div>
                {removable && (
                    <div
                        className={classNames(styles.addContainer, styles.isRemoveButton, {
                            [styles.addContainerCollection]: collectionStyling,
                        })}
                    >
                        <RemoveButton urn={producturn} horizontalMode={horizontalMode} />
                    </div>
                )}
                {!horizontalMode && !removable && (
                    <div
                        className={classNames(styles.addContainer, {
                            [styles.addContainerCollection]: collectionStyling,
                        })}
                    >
                        <AddButton
                            className={styles.add}
                            urn={producturn}
                            removable={removable}
                            horizontalMode={isMobile || isMobileMiddle ? true : horizontalMode}
                        />
                    </div>
                )}
            </div>
        );
    }

    return null;
};

const mapStateToProps = (state: AppState) => ({
    loadingProduct: state.collection.loadingProduct,
});

export default connect(mapStateToProps, null)(Product);
